import React, { useState, useCallback, useRef, useEffect } from "react";
import { uploadFile } from "@/integrations/Core";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Upload, File, X, CheckCircle2, FileText, ArrowUpRight, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { UploadedFile } from "@/entities/UploadedFile";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";

export default function UploadPage() {
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [recentFiles, setRecentFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadRecentFiles = async () => {
      setIsLoading(true);
      try {
        // Load recent files
        const files = await UploadedFile.list("-created_date", 5);
        setRecentFiles(files);
      } catch (error) {
        console.error("Error loading recent files:", error);
      }
      setIsLoading(false);
    };

    loadRecentFiles();
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  }, []);

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const uploadFiles = async () => {
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress((prev) => ({ ...prev, [i]: 0 }));

        try {
          // Usar a nova função de upload que atualiza o progresso
          const result = await uploadFile(file, (progress) => {
            setUploadProgress((prev) => ({
              ...prev,
              [i]: progress,
            }));
          });

          // Não precisamos mais chamar UploadedFile.create pois a função uploadFile já faz isso
        } catch (error) {
          console.error(`Erro ao fazer upload do arquivo ${file.name}:`, error);
        }
      }

      // Redirecionar para a página de arquivos após o upload
      navigate(createPageUrl("UploadedFiles"));
    } catch (error) {
      console.error("Erro ao fazer upload dos arquivos:", error);
    }
    setUploading(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate(createPageUrl("Dashboard"))}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload de Arquivos</h1>
          <p className="text-gray-500">
            Arraste arquivos ou clique para fazer upload
          </p>
        </div>
      </div>

      <div
        className={`border-2 border-dashed rounded-xl p-8 transition-colors ${
          dragActive
            ? "border-sky-200 bg-sky-50"
            : "border-gray-200 hover:border-gray-300"
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <div className="w-20 h-20 bg-sky-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Upload className="w-10 h-10 text-sky-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Arraste seus arquivos aqui
          </h3>
          <p className="text-gray-500 mb-4">
            ou clique para selecionar arquivos do seu computador
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileInput}
            className="hidden"
          />
          <Button
            onClick={handleButtonClick}
            className="bg-sky-600 hover:bg-sky-700 text-white"
          >
            Selecionar Arquivos
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8"
          >
            <h3 className="text-lg font-medium mb-4">Arquivos Selecionados</h3>
            <div className="space-y-3">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                      <File className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {uploading ? (
                      <div className="w-32">
                        <Progress value={uploadProgress[index] || 0} />
                        {uploadProgress[index] === 100 && (
                          <CheckCircle2 className="w-4 h-4 text-green-500 ml-2" />
                        )}
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <Button
                onClick={uploadFiles}
                disabled={uploading}
                className="bg-sky-600 hover:bg-sky-700 text-white"
              >
                {uploading ? "Enviando..." : "Iniciar Upload"}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Seção de Arquivos Recentes */}
      <div className="mt-12">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Arquivos Recentes</h2>
          <Button variant="outline" size="sm" onClick={handleButtonClick}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Arquivo
          </Button>
        </div>
        
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/4 animate-pulse" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : recentFiles.length > 0 ? (
          <div className="space-y-4">
            {recentFiles.map((file) => (
              <motion.div
                key={file.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">{file.original_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>{formatFileSize(file.file_size)}</span>
                          <span>•</span>
                          <span>{formatDate(file.created_date)}</span>
                        </div>
                      </div>
                    </div>
                    <Link to={createPageUrl("UploadedFiles")}>
                      <Button variant="ghost" size="icon">
                        <ArrowUpRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="p-6 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium">Nenhum arquivo encontrado</h3>
            <p className="text-gray-500 mb-4">
              Comece fazendo upload de arquivos
            </p>
            <Button onClick={handleButtonClick}>Fazer Upload</Button>
          </Card>
        )}
        
        {recentFiles.length > 0 && (
          <div className="flex justify-center mt-4">
            <Link to={createPageUrl("UploadedFiles")}>
              <Button variant="outline">
                Ver Todos os Arquivos
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
